import { useLayoutEffect, useRef } from 'react';
import { Tile, type Tone } from '../components/Tile';
import { Icon, type IconName } from '../components/Icon';
import { Dock } from '../components/Dock';
import type { View } from '../types';
import './HomeView.css';

type TileDef = {
  id: string;
  title: string;
  icon: IconName;
  tone: Tone;
  target: View;
};

type Section = {
  id: string;
  title: string;
  tiles: TileDef[];
};

// Teléfono y Spotilisa viven solo en el dock (ver Dock.tsx) — un ícono no
// se duplica entre el dock y la grilla, mismo criterio que un iPhone real.
const SECTIONS: Section[] = [
  {
    id: 'aprender',
    title: 'Aprender',
    tiles: [
      { id: 'todas',     title: 'Tarjetas',   icon: 'all',      tone: 'pink',   target: { name: 'detail', category: 'TODAS' } },
      { id: 'numeros',   title: 'Números',    icon: 'hash',     tone: 'blue',   target: { name: 'detail', category: 'NUMERO' } },
      { id: 'colores',   title: 'Colores',    icon: 'palette',  tone: 'coral',  target: { name: 'detail', category: 'COLOR' } },
      { id: 'formas',    title: 'Formas',     icon: 'shapes',   tone: 'teal',   target: { name: 'detail', category: 'FORMAS' } },
      { id: 'letras',    title: 'Letras',     icon: 'letter',   tone: 'yellow', target: { name: 'letras' } },
      { id: 'silabas',   title: 'Sílabas',    icon: 'syllable', tone: 'lime',   target: { name: 'silabas' } },
      { id: 'cuentos',   title: 'Cuentos',    icon: 'book',     tone: 'rose',   target: { name: 'cuentos-list' } },
    ],
  },
  {
    id: 'jw',
    title: 'JW',
    tiles: [
      { id: 'historias-jw', title: 'Historias JW', icon: 'historia', tone: 'indigo', target: { name: 'jw-historias-list' } },
      { id: 'comics-jw',    title: 'Cómics JW',    icon: 'comic', tone: 'coral',  target: { name: 'jw-comics-list' } },
      { id: 'videos-jw',    title: 'Videos JW',    icon: 'play',  tone: 'blue',   target: { name: 'jw-videos-list' } },
    ],
  },
  {
    id: 'jugar',
    title: 'Jugar',
    tiles: [
      { id: 'memo-easy', title: 'Memo Fácil', icon: 'memo-easy', tone: 'aqua',   target: { name: 'game', game: 'memo-easy' } },
      { id: 'memo',      title: 'Memotest',   icon: 'memo',      tone: 'indigo', target: { name: 'game', game: 'memo' } },
      { id: 'drag',      title: 'Arrastrar',  icon: 'drag',      tone: 'teal',   target: { name: 'game', game: 'drag' } },
      { id: 'oraciones', title: 'Oraciones',  icon: 'sentence',  tone: 'pink',   target: { name: 'game', game: 'oraciones' } },
      { id: 'pizarra',   title: 'Pizarra',    icon: 'pencil',    tone: 'green',  target: { name: 'game', game: 'canvas' } },
      { id: 'voz',       title: 'Voz',        icon: 'mic',       tone: 'purple', target: { name: 'game', game: 'voice' } },
      { id: 'typing',    title: 'Escribir',   icon: 'keyboard',  tone: 'orange', target: { name: 'game', game: 'typing' } },
      { id: 'buscar',    title: 'Buscar',     icon: 'search',    tone: 'violet', target: { name: 'buscar-list' } },
      { id: 'calculadora', title: 'Calculadora', icon: 'calculator', tone: 'dark',   target: { name: 'game', game: 'calculator' } },
      { id: 'contar',    title: 'Contar',     icon: 'count',     tone: 'blue',   target: { name: 'game', game: 'contar' } },
      { id: 'tren',              title: 'El Tren',  icon: 'train',   tone: 'orange', target: { name: 'game', game: 'tren' } },
      { id: 'sonido-inicial',    title: 'Sonidos',  icon: 'sun',     tone: 'yellow', target: { name: 'game', game: 'sonido-inicial' } },
      { id: 'cazador',           title: 'Globos',   icon: 'pop',     tone: 'purple', target: { name: 'game', game: 'cazador' } },
      { id: 'maquina-oraciones', title: 'Máquina',  icon: 'machine', tone: 'teal',   target: { name: 'game', game: 'maquina-oraciones' } },
      { id: 'mente',             title: '¿Qué piensa?', icon: 'mente', tone: 'aqua', target: { name: 'game', game: 'mente' } },
      { id: 'rompecabezas',      title: 'Rompecabezas', icon: 'rompecabezas', tone: 'lime', target: { name: 'game', game: 'rompecabezas' } },
      { id: 'barrera',           title: 'Decile a Mamá', icon: 'charla', tone: 'pink', target: { name: 'game', game: 'barrera' } },
      { id: 'vestir',            title: 'Vestir',   icon: 'shirt',   tone: 'rose', target: { name: 'game', game: 'vestir' } },
      { id: 'pintar',            title: 'Pintar',   icon: 'paint',   tone: 'lime', target: { name: 'game', game: 'pintar' } },
    ],
  },
];

const ALL_TILES: TileDef[] = SECTIONS.flatMap((s) => s.tiles);

type Props = {
  onOpen: (view: View, opts?: { morph?: boolean }) => void;
  poppedFrom?: View | null;
};

export function HomeView({ onOpen, poppedFrom }: Props) {
  const homeRef = useRef<HTMLDivElement>(null);

  // Reverse of Tile.tsx's forward tagging: if we just got popped back to
  // via a morphed tile (poppedFrom is the exact `target` object reference
  // that tile pushed), tag that same tile's icon as the transition's "new"
  // element so it shrinks the incoming view back into itself, instead of a
  // plain cross-fade. Mount-only effect — HomeView only exists in the tree
  // while active, so a fresh mount IS the "just navigated back here" signal.
  useLayoutEffect(() => {
    if (!poppedFrom || !homeRef.current) return;
    const tile = ALL_TILES.find((t) => t.target === poppedFrom);
    if (!tile) return;
    const el = homeRef.current.querySelector<HTMLElement>(`[data-tile-id="${tile.id}"]`);
    if (!el) return;
    el.style.viewTransitionName = 'tile-morph';
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.style.viewTransitionName = '';
      });
    });
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="home" ref={homeRef}>
      <div className="home-scroll-container">
        <div className="home-sections">
          <div className="home-grid">
            {ALL_TILES.map((t) => (
              <Tile
                key={t.id}
                tileId={t.id}
                title={t.title}
                tone={t.tone}
                icon={<Icon name={t.icon} size="100%" />}
                onClick={() => onOpen(t.target, { morph: true })}
              />
            ))}
          </div>
        </div>
      </div>
      <Dock onOpen={onOpen} />
    </div>
  );
}
